import { c as _c } from "react/compiler-runtime";
import { type Address, getChainAddresses, type MarketId } from '@morpho-org/blue-sdk';
import { blueAbi } from '@morpho-org/blue-sdk-viem';
import { ZERO } from '@morpho-org/dapp-core';
import { useReadContract } from 'wagmi';
import { useWeb3Context } from '@/contexts/Web3Context';

/**
 * The single `Morpho.position(marketId, user)` read both gates below share. Extracting it makes
 * the shared wagmi key structural: a poll or args tweak can no longer split them into two
 * subscriptions, or leave the error hook reporting on a query nobody renders.
 */
function useMarketPositionRead(marketId) {
  const $ = _c(17);
  const {
    account,
    targetChainId
  } = useWeb3Context();
  let morpho;
  try {
    let t0;
    if ($[0] !== targetChainId) {
      t0 = getChainAddresses(targetChainId);
      $[0] = targetChainId;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    morpho = t0.blue;
  } catch {
    morpho = undefined;
  }
  let t0;
  if ($[2] !== account.address || $[3] !== marketId) {
    t0 = marketId && account.address ? [marketId, account.address] : undefined;
    $[2] = account.address;
    $[3] = marketId;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const t1 = !!morpho && !!marketId && !!account.address;
  let t2;
  if ($[5] !== t1) {
    t2 = {
      enabled: t1,
      refetchInterval: 10000
    };
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== morpho || $[8] !== t0 || $[9] !== t2 || $[10] !== targetChainId) {
    t3 = {
      address: morpho,
      abi: blueAbi,
      functionName: "position",
      args: t0,
      chainId: targetChainId,
      query: t2
    };
    $[7] = morpho;
    $[8] = t0;
    $[9] = t2;
    $[10] = targetChainId;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    data,
    isError
  } = useReadContract(t3);
  const t4 = account.isConnecting || account.isReconnecting;
  let t5;
  if ($[12] !== account.address || $[13] !== data || $[14] !== isError || $[15] !== t4) {
    t5 = {
      data,
      isError,
      address: account.address,
      isWalletSettling: t4
    };
    $[12] = account.address;
    $[13] = data;
    $[14] = isError;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}

/**
 * Fast Repay-button gate. A single `Morpho.position(marketId, user)` read keyed only on the
 * `marketId` — which is known on mount — so it fires among the **first** calls, without waiting
 * for the API market / `MarketParams` to resolve, and without the heavier
 * `useMarketBlue().getPositionData` multicall (position + market + oracle price). The Repay entry
 * point can therefore appear as soon as the raw position confirms the user holds collateral or
 * debt, rather than after the full position+market read lands.
 *
 * @param marketId - the on-chain `bytes32` market id (available immediately as a panel prop).
 * @returns `true`/`false` once confirmed; `undefined` while the wallet is still connecting or
 *   its read is still pending or has errored — callers must not treat that as a confirmed
 *   absence.
 */
export function useMarketHasPosition(marketId) {
  const {
    data,
    address,
    isWalletSettling
  } = useMarketPositionRead(marketId);
  if (!address) {
    return isWalletSettling ? undefined : false;
  }
  if (!data) {
    return;
  }
  return data[1] > ZERO || data[2] > ZERO;
}

/**
 * Whether the shared `Morpho.position` read above has failed rather than merely not landed.
 *
 * Both availability gates answer `undefined` for a pending read and an errored one, which is
 * right for the gates (neither is a confirmed absence) but leaves a caller unable to tell a slow
 * read from a broken one.
 */
export function useMarketPositionReadError(marketId) {
  const {
    data,
    isError
  } = useMarketPositionRead(marketId);
  return isError && !data;
}